// src/components/SEO.jsx
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  noindex = false,
  basePath = '' // ex: '/build' si ton site est servi sous /build
}) => {
  const location = useLocation();
  const baseUrl = 'https://dp-architecture.com';

  const currentUrl = `${baseUrl}${basePath}${location.pathname}`;

  // Valeurs par défaut
  const defaultTitle =
    'Daniela Parito Architecture - Architecte à Montpellier | Hérault (34)';
  const defaultDescription =
    "Cabinet d'architecture à Montpellier spécialisé en logements collectifs, maisons individuelles et aménagement territorial. 30 ans d'expérience en Occitanie.";
  const defaultKeywords =
    'architecte montpellier, agence architecture montpellier, architecte hérault, logement collectif, maison individuelle';
  const defaultImage = `${baseUrl}/og-image.jpg`;

  const seoTitle = title || defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoKeywords = keywords || defaultKeywords;
  const seoImage = image || defaultImage;

  useEffect(() => {
    document.title = seoTitle;

    // Meta
    updateMetaTag('name', 'description', seoDescription);
    updateMetaTag('name', 'keywords', seoKeywords);

    // Robots - always set explicitly for Google
    if (noindex) {
      updateMetaTag('name', 'robots', 'noindex, follow');
    } else {
      updateMetaTag('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Open Graph
    updateMetaTag('property', 'og:title', seoTitle);
    updateMetaTag('property', 'og:description', seoDescription);
    updateMetaTag('property', 'og:url', currentUrl);
    updateMetaTag('property', 'og:image', seoImage);
    updateMetaTag('property', 'og:type', type);
    updateMetaTag('property', 'og:locale', 'fr_FR');
    updateMetaTag('property', 'og:site_name', 'Daniela Parito Architecture');

    // Twitter
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', seoTitle);
    updateMetaTag('name', 'twitter:description', seoDescription);
    updateMetaTag('name', 'twitter:image', seoImage);

    // Canonical - normalize URL (remove trailing slash except root)
    const normalizedUrl = currentUrl.endsWith('/') && currentUrl !== `${baseUrl}/`
      ? currentUrl.slice(0, -1)
      : currentUrl;
    updateCanonical(normalizedUrl);
  }, [seoTitle, seoDescription, seoKeywords, seoImage, currentUrl, type, noindex]);

  return null;
};

function updateMetaTag(attribute, key, content) {
  if (!content) return;

  let element = document.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}


function updateCanonical(url) {
  let link = document.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', url);
}

export default SEO;

// ==============================================
// CONFIGURATION SEO PAR PAGE
// ==============================================

export const seoConfig = {
  home: {
    title: 'Daniela Parito Architecture - Architecte à Montpellier | Hérault (34)',
    description: "Cabinet d'architecture à Montpellier. Logements collectifs, maisons individuelles, aménagement territorial. 30 ans d'expérience. RE2020, BBC, passif.",
    keywords: 'architecte montpellier, agence architecture montpellier, cabinet architecture, architecte hérault, logement collectif montpellier, maison individuelle montpellier, RE2020, BBC'
  },
  
  agency: {
    title: "L'Agence - Daniela Parito Architecture | Architecte Montpellier",
    description: "Cabinet d'architecture fondé en 2014 à Montpellier. Daniela Parito, architecte, 30 ans d'expérience en logements collectifs, individuels et équipements publics.",
    keywords: 'agence architecture montpellier, architecte montpellier, daniela parito, cabinet architecture hérault, architecte occitanie'
  },
  
  projects: {
    title: 'Nos Projets - Réalisations Architecture Montpellier | Hérault',
    description: 'Découvrez nos réalisations en logements collectifs, maisons individuelles et équipements publics à Montpellier et dans l\'Hérault. Plus de 150 projets livrés.',
    keywords: 'projets architecture montpellier, réalisations architecte hérault, logement collectif montpellier, maison contemporaine montpellier, architecture occitanie'
  },
  
  missions: {
    title: 'Nos missions d\'architecte à Montpellier | DP Architecture',
    description: 'Conception architecturale, logements collectifs, maisons individuelles, aménagement territorial. Expertise RE2020, BBC, passif. Montpellier et Occitanie.',
    keywords: 'architecte logement collectif montpellier, architecte maison individuelle hérault, conception architecturale, RE2020 montpellier, BBC passif, ZAC montpellier'
  },
  
  contact: {
    title: 'Contact - Architecte Montpellier | Devis Gratuit',
    description: 'Contactez Daniela Parito Architecture à Montpellier pour votre projet de logement collectif, maison individuelle ou aménagement. Devis gratuit.',
    keywords: 'contact architecte montpellier, devis architecture montpellier, architecte hérault contact, architecte montpellier 34000'
  },
  
  news: {
    title: 'Actualités - Projets et Réalisations | Architecte Montpellier',
    description: 'Suivez les derniers projets et réalisations de Daniela Parito Architecture à Montpellier et dans l\'Hérault.',
    keywords: 'actualités architecture montpellier, projets architecte hérault, livraison logements montpellier'
  }
};